import { forwardRef, useId, type TextareaHTMLAttributes } from "react";
import { cn } from "./lib/cn.js";

/**
 * The big editable surface that holds the system prompt — the
 * markdown content equivalent to `agents/prompts/<name>.md` in the
 * repo. Monaco is deferred per `docs/client/architecture.md §4.1`;
 * the MVP is a textarea with a character count and a rough token
 * estimate (chars ÷ 4, which tracks the OpenAI / Anthropic guidance
 * closely enough to flag a runaway prompt before submit).
 *
 * Optional helper banner: the architecture doc calls out a linter
 * (`§4.3 Prompt linting`) that flags missing heat instructions, tool
 * references with no matching pick, prompt-too-long, etc. That linter
 * lives outside this component — pass `warnings` and we'll render a
 * row of error chips beneath the editor. Empty array = no warnings.
 */
export interface SystemPromptEditorProps
 extends Omit<TextareaHTMLAttributes<HTMLTextAreaElement>, "value" | "onChange"> {
 value: string;
 onChange: (value: string) => void;
 /** Optional warnings to surface beneath the editor. Typically
 * filled in by the linter (§4.3) — empty array suppresses the row. */
 warnings?: readonly string[];
 /** Token budget for the active model — shown alongside the
 * estimate so kj can eyeball headroom. Skip to hide. */
 tokenBudget?: number;
 /** Label text (defaults to "System prompt"). */
 label?: string;
}

/** Rough char→token heuristic. Both OpenAI and Anthropic publish
 * ~4 chars per token for English; close enough for an in-editor
 * budget meter. The real token count happens server-side when the
 * prompt actually gets tokenised. */
function estimateTokens(value: string): number {
 return Math.ceil(value.length / 4);
}

export const SystemPromptEditor = forwardRef<
 HTMLTextAreaElement,
 SystemPromptEditorProps
>(
 (
 {
 value,
 onChange,
 warnings,
 tokenBudget,
 label = "System prompt",
 className,
 placeholder = "# Agent name — persona\n\nYou're a …",
 rows = 18,
 ...rest
 },
 ref,
 ) => {
 const id = useId();
 const chars = value.length;
 const tokens = estimateTokens(value);
 const over = tokenBudget != null && tokens > tokenBudget;

 return (
 <div className={cn("flex flex-col gap-2", className)}>
 <div className="flex items-baseline justify-between gap-3">
 <label
 htmlFor={id}
 className="font-[var(--pp-font-family)] text-[var(--pp-font-sm)] uppercase tracking-[var(--pp-tracking-caps)] text-[var(--pp-text-muted)]"
 >
 {label}
 </label>
 <span
 className={cn(
 "font-[var(--pp-font-family)] text-[var(--pp-font-sm)] tabular-nums",
 over
 ? "text-[var(--pp-danger)]"
 : "text-[var(--pp-text-muted)]",
 )}
 role="status"
 aria-live="polite"
 >
 {chars.toLocaleString()} chars · ~{tokens.toLocaleString()} tok
 {tokenBudget != null && (
 <span> / {tokenBudget.toLocaleString()}</span>
 )}
 </span>
 </div>
 <textarea
 ref={ref}
 id={id}
 value={value}
 onChange={(e) => onChange(e.currentTarget.value)}
 rows={rows}
 placeholder={placeholder}
 spellCheck={false}
 className={cn(
 "w-full border bg-transparent p-3",
 "font-[var(--pp-font-family)] text-[var(--pp-font-md)] leading-relaxed text-[var(--pp-text)]",
 "placeholder:text-[var(--pp-text-muted)]",
 "focus:outline-none focus:ring-2 focus:ring-[var(--primary)] focus:ring-offset-2 focus:ring-offset-[var(--background)]",
 over
 ? "border-[var(--pp-danger)]"
 : "border-[var(--pp-border)]",
 )}
 aria-invalid={over || (warnings && warnings.length > 0) || undefined}
 {...rest}
 />
 {warnings && warnings.length > 0 && (
 <ul
 className="flex flex-col gap-1 text-[var(--pp-font-sm)] text-[var(--pp-danger)]"
 aria-label="Prompt linter warnings"
 >
 {warnings.map((w) => (
 <li key={w} className="font-[var(--pp-font-family)]">
 ! {w}
 </li>
 ))}
 </ul>
 )}
 {over && tokenBudget != null && (
 <p className="text-[var(--pp-font-sm)] text-[var(--pp-danger)]">
 Over budget — small models may struggle to follow. Trim ~
 {(tokens - tokenBudget).toLocaleString()} tokens to land
 under {tokenBudget.toLocaleString()}.
 </p>
 )}
 </div>
 );
 },
);
SystemPromptEditor.displayName = "SystemPromptEditor";
